/**
 * ClinicalHUD3 preferences. Storage keys use the clinicalHud3. prefix only.
 * This module does not read or write any other product's preference keys.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

export const REFRESH_INTERVAL_SECONDS = Object.freeze([7, 12, 18, 24]);

export const DEFAULT_REFRESH_INTERVAL_SEC = 7;

export const REFRESH_INTERVAL_STORAGE_KEY = "clinicalHud3.refreshIntervalSec";

/**
 * When true, Differential mode uses ChartMind's LLM path.
 * Default On.
 */
export const CHARTMIND_DDX_STORAGE_KEY = "clinicalHud3.chartMindDdx";

export const DEFAULT_CHARTMIND_DDX = true;

export const DDX_ENGINE_LEGACY = "legacy";

export const DDX_ENGINE_UPGRADED = "upgraded";

export const DDX_ENGINE_UPGRADED_PLUS = "upgradedPlus";

export const DDX_ENGINE_STORAGE_KEY = "clinicalHud3.ddxEngine";

export const DEFAULT_DDX_ENGINE = DDX_ENGINE_LEGACY;

export const DDX_ENGINE_OPTIONS = Object.freeze([
  DDX_ENGINE_LEGACY,
  DDX_ENGINE_UPGRADED,
  DDX_ENGINE_UPGRADED_PLUS,
]);

/**
 * When true, Live may show an Expert Advice shortcut. Default Off.
 * Teaching config still hides the control.
 */
export const EXPERT_ADVICE_STORAGE_KEY = "clinicalHud3.expertAdviceEnabled";

export const DEFAULT_EXPERT_ADVICE_ENABLED = false;

export function normalizeRefreshInterval(value) {
  const n = Number(value);
  return REFRESH_INTERVAL_SECONDS.includes(n) ? n : DEFAULT_REFRESH_INTERVAL_SEC;
}

export function readStoredRefreshInterval() {
  try {
    return normalizeRefreshInterval(
      window.localStorage.getItem(REFRESH_INTERVAL_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_REFRESH_INTERVAL_SEC;
  }
}

export function writeStoredRefreshInterval(seconds) {
  const n = normalizeRefreshInterval(seconds);
  try {
    window.localStorage.setItem(REFRESH_INTERVAL_STORAGE_KEY, String(n));
  } catch {
    /* ignore quota / private mode */
  }
  return n;
}

export function normalizeChartMindDdx(value) {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  return DEFAULT_CHARTMIND_DDX;
}

export function readStoredChartMindDdx() {
  try {
    const raw = window.localStorage.getItem(CHARTMIND_DDX_STORAGE_KEY);
    if (raw === null) return DEFAULT_CHARTMIND_DDX;
    return normalizeChartMindDdx(raw);
  } catch {
    return DEFAULT_CHARTMIND_DDX;
  }
}

export function writeStoredChartMindDdx(enabled) {
  const next = normalizeChartMindDdx(enabled);
  try {
    window.localStorage.setItem(CHARTMIND_DDX_STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeDdxEngine(value) {
  if (value === DDX_ENGINE_UPGRADED_PLUS || value === "upgraded-plus") {
    return DDX_ENGINE_UPGRADED_PLUS;
  }
  if (value === DDX_ENGINE_UPGRADED) return DDX_ENGINE_UPGRADED;
  return DDX_ENGINE_LEGACY;
}

export function readStoredDdxEngine() {
  try {
    const raw = window.localStorage.getItem(DDX_ENGINE_STORAGE_KEY);
    if (raw === null) return DEFAULT_DDX_ENGINE;
    return normalizeDdxEngine(raw);
  } catch {
    return DEFAULT_DDX_ENGINE;
  }
}

export function writeStoredDdxEngine(engine) {
  const next = normalizeDdxEngine(engine);
  try {
    window.localStorage.setItem(DDX_ENGINE_STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeExpertAdviceEnabled(value) {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  return DEFAULT_EXPERT_ADVICE_ENABLED;
}

export function readStoredExpertAdviceEnabled() {
  try {
    const raw = window.localStorage.getItem(EXPERT_ADVICE_STORAGE_KEY);
    if (raw === null) return DEFAULT_EXPERT_ADVICE_ENABLED;
    return normalizeExpertAdviceEnabled(raw);
  } catch {
    return DEFAULT_EXPERT_ADVICE_ENABLED;
  }
}

export function writeStoredExpertAdviceEnabled(enabled) {
  const next = normalizeExpertAdviceEnabled(enabled);
  try {
    window.localStorage.setItem(EXPERT_ADVICE_STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeHud3Transcript(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

/**
 * True when the encounter text has changed enough to refresh DDx / Pearls.
 * The page interval is the time gate; this only ignores blank / identical text.
 */
export function shouldRefreshHud3Transcript(lastAnalyzed, current) {
  const next = normalizeHud3Transcript(current);
  if (!next) return false;
  return next !== normalizeHud3Transcript(lastAnalyzed);
}

const ClinicalHud3SettingsContext = createContext(null);

export function ClinicalHud3SettingsProvider({ children }) {
  const [seconds, setSecondsState] = useState(readStoredRefreshInterval);
  const [chartMindDdx, setChartMindDdxState] = useState(readStoredChartMindDdx);
  const [ddxEngine, setDdxEngineState] = useState(readStoredDdxEngine);
  const [expertAdviceEnabled, setExpertAdviceEnabledState] = useState(
    readStoredExpertAdviceEnabled,
  );

  const setSeconds = useCallback((next) => {
    setSecondsState(writeStoredRefreshInterval(next));
  }, []);

  const setChartMindDdx = useCallback((next) => {
    setChartMindDdxState(writeStoredChartMindDdx(next));
  }, []);

  const setDdxEngine = useCallback((next) => {
    setDdxEngineState(writeStoredDdxEngine(next));
  }, []);

  const setExpertAdviceEnabled = useCallback((next) => {
    setExpertAdviceEnabledState(writeStoredExpertAdviceEnabled(next));
  }, []);

  const value = useMemo(
    () => ({
      seconds,
      setSeconds,
      options: REFRESH_INTERVAL_SECONDS,
      chartMindDdx,
      setChartMindDdx,
      ddxEngine,
      setDdxEngine,
      expertAdviceEnabled,
      setExpertAdviceEnabled,
    }),
    [
      seconds,
      setSeconds,
      chartMindDdx,
      setChartMindDdx,
      ddxEngine,
      setDdxEngine,
      expertAdviceEnabled,
      setExpertAdviceEnabled,
    ],
  );

  return (
    <ClinicalHud3SettingsContext.Provider value={value}>
      {children}
    </ClinicalHud3SettingsContext.Provider>
  );
}

ClinicalHud3SettingsProvider.propTypes = {
  children: PropTypes.node,
};

export function useClinicalHud3Settings() {
  const ctx = useContext(ClinicalHud3SettingsContext);
  if (!ctx) {
    return {
      seconds: DEFAULT_REFRESH_INTERVAL_SEC,
      setSeconds: () => {},
      options: REFRESH_INTERVAL_SECONDS,
      chartMindDdx: DEFAULT_CHARTMIND_DDX,
      setChartMindDdx: () => {},
      ddxEngine: DEFAULT_DDX_ENGINE,
      setDdxEngine: () => {},
      expertAdviceEnabled: DEFAULT_EXPERT_ADVICE_ENABLED,
      setExpertAdviceEnabled: () => {},
    };
  }
  return ctx;
}
